const likelyLocalFilesystemPathPattern =
  /^(?:~\/|file:\/\/|[A-Za-z]:[\\/]|(?:\.\/)?\.willow(?:\/|$)|\/\.willow(?:\/|$)|\/(?:Users|private|var|tmp|home|Volumes)(?:\/|$))/;

const authenticatedAttachmentRoutePrefixes = [
  '/v1/user/agent/files/',
  '/v1/agents/',
  '/v1/storage/',
  '/v1/public/shared-chats/',
] as const;

export interface AttachmentDisplayURLParams {
  src?: string | null;
  displayURL?: string | null;
  displayUrl?: string | null;
  display_url?: string | null;
  downloadURL?: string | null;
  downloadUrl?: string | null;
  download_url?: string | null;
  thumbnailURL?: string | null;
  thumbnailUrl?: string | null;
  thumbnail_url?: string | null;
  publicShareURL?: string | null;
  publicShareUrl?: string | null;
  public_share_url?: string | null;
  authRequired?: boolean | null;
  auth_required?: boolean | null;
  authScope?: string | null;
  auth_scope?: string | null;
  preferSignedAgentFileShare?: boolean | null;
  filePath?: string | null;
  environmentId?: string | null;
  agentId?: string | null;
  signature?: string | null;
  fileRefs?: ReadonlyArray<AttachmentFileRef> | null;
}

export interface AttachmentFileRef {
  refId?: string | null;
  ref_id?: string | null;
  kind?: string | null;
  source?: string | null;
  path?: string | null;
  displayURL?: string | null;
  displayUrl?: string | null;
  display_url?: string | null;
  downloadURL?: string | null;
  downloadUrl?: string | null;
  download_url?: string | null;
  thumbnailURL?: string | null;
  thumbnailUrl?: string | null;
  thumbnail_url?: string | null;
  publicShareURL?: string | null;
  publicShareUrl?: string | null;
  public_share_url?: string | null;
  authRequired?: boolean | null;
  auth_required?: boolean | null;
  authScope?: string | null;
  auth_scope?: string | null;
  name?: string | null;
  mimeType?: string | null;
  mime_type?: string | null;
  sizeBytes?: number | null;
  size_bytes?: number | null;
  contentHash?: string | null;
  content_hash?: string | null;
  environmentId?: string | null;
  environment_id?: string | null;
  agentId?: string | null;
  agent_id?: string | null;
  signature?: string | null;
}

export function attachmentFileRefSignature(ref: AttachmentFileRef): string {
  return [
    ref.refId?.trim() ?? ref.ref_id?.trim() ?? '',
    ref.kind?.trim() ?? '',
    ref.source?.trim() ?? '',
    ref.path?.trim() ?? '',
    ref.displayURL?.trim() ??
      ref.displayUrl?.trim() ??
      ref.display_url?.trim() ??
      '',
    ref.downloadURL?.trim() ??
      ref.downloadUrl?.trim() ??
      ref.download_url?.trim() ??
      '',
    ref.thumbnailURL?.trim() ??
      ref.thumbnailUrl?.trim() ??
      ref.thumbnail_url?.trim() ??
      '',
    ref.publicShareURL?.trim() ??
      ref.publicShareUrl?.trim() ??
      ref.public_share_url?.trim() ??
      '',
    String(ref.authRequired ?? ref.auth_required ?? ''),
    ref.authScope?.trim() ?? ref.auth_scope?.trim() ?? '',
    ref.name?.trim() ?? '',
    ref.mimeType?.trim() ?? ref.mime_type?.trim() ?? '',
    ref.sizeBytes ?? ref.size_bytes ?? '',
    ref.contentHash?.trim() ?? ref.content_hash?.trim() ?? '',
    ref.environmentId?.trim() ?? ref.environment_id?.trim() ?? '',
    ref.agentId?.trim() ?? ref.agent_id?.trim() ?? '',
    ref.signature?.trim() ?? '',
  ].join('\u0000');
}

export function attachmentFileRefsSignature(
  refs: ReadonlyArray<AttachmentFileRef> | null | undefined
): string {
  return (refs ?? []).map(attachmentFileRefSignature).join('\u0001');
}

export function isLikelyLocalFilesystemPath(value: string): boolean {
  return likelyLocalFilesystemPathPattern.test(value);
}

function isDirectBrowserAttachmentURL(value: string): boolean {
  if (isLikelyLocalFilesystemPath(value)) {
    return false;
  }
  return (
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/v1/') ||
    value.startsWith('./') ||
    value.startsWith('../')
  );
}

export function normalizeAgentPath(filePath: string): string {
  const trimmed = filePath.trim().replace(/\\/g, '/');
  if (!trimmed || trimmed === '.') {
    return '/';
  }

  const segments: string[] = [];
  for (const segment of trimmed.split('/')) {
    if (!segment || segment === '.') {
      continue;
    }
    if (segment === '..') {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }

  return segments.length > 0 ? `/${segments.join('/')}` : '/';
}

export function buildAgentFileURL(filePath: string): string {
  return `/v1/user/agent/files${normalizeAgentPath(filePath)}`;
}

export function buildAgentFileShareURL(
  agentId: string,
  signature: string
): string {
  return `/v1/agents/${encodeURIComponent(agentId.trim())}/file-shares/${encodeURIComponent(signature.trim())}`;
}

export function isAgentFileShareRoute(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  const path = (() => {
    if (trimmed.startsWith('/v1/')) {
      return trimmed;
    }
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      return null;
    }
    try {
      return new URL(trimmed).pathname;
    } catch {
      return null;
    }
  })();

  return /^\/v1\/agents\/[^/]+\/file-shares\/[^/?#]+/.test(path ?? '');
}

export function isVFSLikeEnvironmentId(
  value: string | null | undefined
): boolean {
  const normalized = value?.trim().replaceAll('_', '-').toLowerCase() ?? '';
  return (
    normalized === '' || normalized === 'vfs' || normalized === 'agent-vfs'
  );
}

function isLocalOnlyEnvironmentId(value: string | null | undefined): boolean {
  const normalized = value?.trim().replaceAll('_', '-').toLowerCase() ?? '';
  return normalized === 'local-vm' || normalized.startsWith('local-vm-');
}

function hasExplicitVFSLikeEnvironmentId(
  value: string | null | undefined
): boolean {
  const normalized = value?.trim().replaceAll('_', '-').toLowerCase() ?? '';
  return normalized === 'vfs' || normalized === 'agent-vfs';
}

function isWillowWorkspacePath(value: string): boolean {
  const normalized = value.trim().replace(/\\/g, '/');
  return (
    normalized.startsWith('/.willow/') ||
    normalized === '/.willow' ||
    normalized.startsWith('.willow/') ||
    normalized === '.willow'
  );
}

function firstCanonicalDeliveryURL(
  params: AttachmentDisplayURLParams
): string | null {
  for (const candidate of [
    params.displayURL,
    params.displayUrl,
    params.display_url,
    params.publicShareURL,
    params.publicShareUrl,
    params.public_share_url,
    params.downloadURL,
    params.downloadUrl,
    params.download_url,
  ]) {
    const route = normalizeCanonicalDeliveryURL(candidate);
    if (route) {
      return route;
    }
  }

  for (const ref of params.fileRefs ?? []) {
    for (const candidate of [
      ref.displayURL,
      ref.displayUrl,
      ref.display_url,
      ref.publicShareURL,
      ref.publicShareUrl,
      ref.public_share_url,
      ref.downloadURL,
      ref.downloadUrl,
      ref.download_url,
    ]) {
      const route = normalizeCanonicalDeliveryURL(candidate);
      if (route) {
        return route;
      }
    }

    const signedRoute = signedAgentFileShareURLFromRef(ref);
    if (signedRoute) {
      return signedRoute;
    }
  }

  for (const ref of params.fileRefs ?? []) {
    const route = agentFileURLFromRef(ref);
    if (route) {
      return route;
    }
  }

  return null;
}

function firstSignedAgentFileShareURL(
  params: AttachmentDisplayURLParams
): string | null {
  const directRoute = signedAgentFileShareURLFromParams(params);
  if (directRoute) {
    return directRoute;
  }

  for (const ref of params.fileRefs ?? []) {
    const route = signedAgentFileShareURLFromRef(ref);
    if (route) {
      return route;
    }
  }

  return null;
}

function signedAgentFileShareURLFromRef(ref: AttachmentFileRef): string | null {
  const agentId = ref.agentId?.trim() ?? ref.agent_id?.trim() ?? '';
  const signature = ref.signature?.trim() ?? '';
  const path = ref.path?.trim() ?? '';
  if (!agentId || !signature || !path) {
    return null;
  }
  if (!isVFSLikeEnvironmentId(ref.environmentId ?? ref.environment_id)) {
    return null;
  }
  return buildAgentFileShareURL(agentId, signature);
}

function agentFileURLFromRef(ref: AttachmentFileRef): string | null {
  const path = ref.path?.trim();
  if (!path) {
    return null;
  }
  if (isLocalOnlyEnvironmentId(ref.environmentId ?? ref.environment_id)) {
    return null;
  }
  if (!isVFSLikeEnvironmentId(ref.environmentId ?? ref.environment_id)) {
    return null;
  }
  return buildAgentFileURL(path);
}

function signedAgentFileShareURLFromParams(
  params: AttachmentDisplayURLParams
): string | null {
  const agentId = params.agentId?.trim() ?? '';
  const signature = params.signature?.trim() ?? '';
  const path = params.filePath?.trim() ?? '';
  if (!agentId || !signature || !path) {
    return null;
  }
  if (!isVFSLikeEnvironmentId(params.environmentId)) {
    return null;
  }
  return buildAgentFileShareURL(agentId, signature);
}

function agentFileURLFromParams(
  params: AttachmentDisplayURLParams
): string | null {
  const path = params.filePath?.trim();
  if (!path) {
    return null;
  }
  if (isLocalOnlyEnvironmentId(params.environmentId)) {
    return null;
  }
  if (
    !hasExplicitVFSLikeEnvironmentId(params.environmentId) &&
    !isWillowWorkspacePath(path)
  ) {
    return null;
  }
  return buildAgentFileURL(path);
}

function normalizeAuthenticatedAttachmentRoute(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  for (const prefix of authenticatedAttachmentRoutePrefixes) {
    if (trimmed.startsWith(prefix)) {
      return trimmed;
    }
  }

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return null;
  }

  try {
    const url = new URL(trimmed);
    for (const prefix of authenticatedAttachmentRoutePrefixes) {
      if (!url.pathname.startsWith(prefix)) {
        continue;
      }
      return url.search ? `${url.pathname}${url.search}` : url.pathname;
    }
  } catch {
    return null;
  }

  return null;
}

function normalizeCanonicalDeliveryURL(
  value: string | null | undefined
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const authenticatedRoute = normalizeAuthenticatedAttachmentRoute(trimmed);
  if (authenticatedRoute) {
    return authenticatedRoute;
  }
  if (isDirectBrowserAttachmentURL(trimmed)) {
    return trimmed;
  }
  return null;
}

export function requiresNativeURLResolution(value: string): boolean {
  return normalizeAuthenticatedAttachmentRoute(value) !== null;
}

export function resolveAttachmentDisplayURL(
  params: AttachmentDisplayURLParams
): string | null {
  if (params.preferSignedAgentFileShare) {
    const signedRoute = firstSignedAgentFileShareURL(params);
    if (signedRoute) {
      return signedRoute;
    }
  }

  const canonical = firstCanonicalDeliveryURL(params);
  if (canonical) {
    return canonical;
  }

  const publicSharedRoute = params.src
    ? normalizeAuthenticatedAttachmentRoute(params.src)
    : null;
  if (publicSharedRoute?.startsWith('/v1/public/shared-chats/')) {
    return publicSharedRoute;
  }

  const browserSource = resolveBrowserAttachmentURL({ src: params.src });
  if (browserSource) {
    return browserSource;
  }

  const signedAgentFileShareURL = signedAgentFileShareURLFromParams(params);
  if (signedAgentFileShareURL) {
    return signedAgentFileShareURL;
  }

  const agentFileURL = agentFileURLFromParams(params);
  if (agentFileURL) {
    return agentFileURL;
  }

  return null;
}

export function resolveBrowserAttachmentURL(params: {
  src?: string | null;
}): string | null {
  const src = params.src?.trim();

  if (src?.startsWith('data:')) {
    return src;
  }

  const authenticatedRoute = src
    ? normalizeAuthenticatedAttachmentRoute(src)
    : null;
  if (authenticatedRoute) {
    return authenticatedRoute;
  }

  if (src && isDirectBrowserAttachmentURL(src)) {
    return src;
  }

  if (src && !isLikelyLocalFilesystemPath(src)) {
    return src;
  }

  return null;
}
